/**
 * FromSection — basic FROM clause rendering. Each comma-separated FROM
 * segment renders its leading FROM item as a chip sized to its text. A single
 * JOIN sits on the same line, to its right, the way the SQL reads
 * (`orders o INNER JOIN customers c ON …`), and wraps below it when the line
 * has no room; two or more JOINs stack beneath it. A derived table (a
 * subquery block) keeps the full-width row with its JOINs beneath. In RO mode
 * every affordance is disabled
 * (× / [+ add table] / [+ add join] / drop target). When VQE-Edit supplies
 * an `editContext` prop AND the resolver service has a schema snapshot, the
 * [+ add table] popover is populated by `useTableSuggestions`, the
 * SchemaTreeDropTarget accepts a JSON dataTransfer payload, and per-row ×
 * commits a `remove-from-item` edit (with a confirm prompt when removing
 * the only data source).
 */

import type { ReactNode } from 'react';

import { useAnalytics } from '../../../../../core/analytics/useAnalytics';
import type { ParseOptions } from '../../../../sql-grammar/model';
import type { VqeEditContext } from '../../edit/editContext';
import { toCommitCtx } from '../../edit/editContext';
import { addRelationExpression } from '../../edit/edits/addTable';
import { formatRelationRef } from '../../edit/edits/fromItemHelpers';
import { parseVqeDragTablePayload } from '../common/dragSchemaPayload';
import { SchemaTreeDropTarget } from '../common/SchemaTreeDropTarget';
import { vqeCopy } from '../../ux/vqeCopy';
import { ClauseCard, EMPTY_CLAUSE_LABEL } from './ClauseCard';
import { extractFromSegmentJoins } from './extractFromJoins';
import type { SelectOutline } from './extractSelectOutline';
import { extractSubqueries } from './extractSubqueries';
import type { CteNameEntry } from './fromCteRef';
import { FromAddTable } from './FromAddTable';
import { FromItemRow } from './FromItemRow';
import { computeJoinOffsets, resolveJoinAnchor } from './fromSectionHelpers';
import { JoinAddPopover } from './JoinAddPopover';
import { JoinLine } from './JoinLine';
import type { SubqueryWiring } from './SelectRenderer';
import { splitTopLevelCommas } from './splitTopLevel';

export interface FromSectionProps {
  readonly bufferText: string;
  readonly outline: SelectOutline;
  readonly options: ParseOptions;
  readonly subqueryCtx?: SubqueryWiring;
  readonly editContext?: VqeEditContext;
  /**
   * CTE names declared by the enclosing WITH clause (threaded from
   * `SelectRenderer`'s already-extracted `CteOutline` — see `fromCteRef.ts`).
   * Passed through to each `FromItemRow` for the CTE-ref badge's name-match
   * fallback (used when no edit-ready `QueryStructure` is reachable).
   */
  readonly cteNames?: readonly CteNameEntry[];
  /**
   * SEL-22: threads down to each `<JoinLine>` for the dense single-line
   * compact-popover JOIN layout. Falsy (the default) leaves every JoinLine in
   * full mode — unchanged.
   */
  readonly compact?: boolean;
  /**
   * Extra actions appended to the card's action line after Add join / Add
   * cross-join — the full layout's "Add CTE" when the statement has no WITH
   * clause yet (`SelectRenderer`).
   */
  readonly extraActions?: ReactNode;
}

export function FromSection({
  bufferText,
  outline,
  options,
  subqueryCtx,
  editContext,
  cteNames,
  compact,
  extraActions,
}: FromSectionProps) {
  const { track } = useAnalytics();

  // No FROM yet (e.g. `SELECT 1`): a one-line keyword row like the empty WHERE
  // — "FROM — none · + Add table" — so a table can be added at all. The
  // `add-from-item` edit synthesizes the FROM clause after the select list.
  // Read-only mounts (no edit context) keep rendering nothing.
  if (!outline.from) {
    if (!editContext) return null;
    return (
      <ClauseCard
        title="From"
        testId="vqe-from-row"
        density="compact"
        footer={
          <div className="flex flex-wrap gap-2" data-testid="vqe-from-actions">
            <FromAddTable
              editContext={editContext}
              itemCount={0}
              triggerLabel={vqeCopy.actions.addTable}
              testId="vqe-from-add-first-table"
            />
            {extraActions}
          </div>
        }
      >
        <span className="text-xs italic text-muted-foreground">{EMPTY_CLAUSE_LABEL}</span>
      </ClauseCard>
    );
  }
  const segments = splitTopLevelCommas(bufferText, outline.from.start, outline.from.end, options).map((item) => ({
    rangeStart: item.start,
    rangeEnd: item.end,
    rawText: item.text,
    split: extractFromSegmentJoins(bufferText, item.start, item.end, options),
  }));

  const editReady = !!editContext && !!editContext.service.getSchemaSnapshot();

  const onDrop = editReady && editContext
    ? (data: DataTransfer) => {
        const payload = parseVqeDragTablePayload(data);
        if (!payload) return;
        const text = formatRelationRef({
          name: payload.name,
          raw: payload.name,
          quoted: !!payload.quoted,
          foldedName: payload.name.toLowerCase(),
          schema: payload.schema,
          foldedSchema: payload.schema?.toLowerCase(),
        });
        // Success analytics is OUTCOME-DEPENDENT: `commitEdit` no longer rejects
        // on a commit failure (P12 §4.2), so branch on the `CommitOutcome` —
        // fire `vqe_drag_add_table` only when the edit actually reached the
        // buffer. The `.catch` covers only `addRelationExpression`'s own
        // rejection path (there is none today, but this mirrors the wrapper
        // pre-validation idiom used by the other migrated call sites).
        void addRelationExpression(text, toCommitCtx(editContext))
          .then((outcome) => {
            if (outcome.status === 'applied') track('feature:vqe_drag_add_table', {});
          })
          .catch(() => {});
      }
    : undefined;

  // For the add-join popover we need the leading FROM-item's qualified name +
  // user-visible alias. Pull from the live QueryStructure so we honour the
  // current parse (post-edits) rather than re-deriving from text.
  const joinAnchor = editReady && editContext ? resolveJoinAnchor(editContext) : undefined;

  // Cumulative join offset per segment so each rendered JoinLine can map its
  // local jIdx → flat QueryStructure.joins[index].
  const joinOffsets = computeJoinOffsets(segments);

  return (
    <ClauseCard
      title="From"
      testId="vqe-from-section"
      density="line"
      // The actions follow the last FROM / JOIN row on the title's line when
      // they fit, and wrap below it otherwise.
      footer={
        // [+ add join] (FK-driven, for related tables) leads; [+ add cross-join]
        // (bare comma table → Cartesian product) is the secondary affordance.
        <div className="flex flex-wrap gap-2" data-testid="vqe-from-actions">
          <JoinAddPopover
            editContext={editContext}
            leftTable={joinAnchor?.qualified}
            leftAlias={joinAnchor?.alias}
          />
          <FromAddTable editContext={editContext} itemCount={segments.length} />
          {extraActions}
        </div>
      }
    >
      <SchemaTreeDropTarget
        testId="vqe-from-drop-target"
        enabled={editReady}
        onDrop={onDrop}
      >
      {segments.length === 0 ? (
        <p className="text-xs italic text-muted-foreground" data-testid="vqe-from-empty">
          No tables parsed.
        </p>
      ) : (
        <ul className="flex flex-col gap-2" data-testid="vqe-from-items">
          {segments.map((seg, idx) => {
            const leading = seg.split.leading;
            const leadingText = leading.text || seg.rawText;
            const derived = subqueryCtx
              ? extractSubqueries(bufferText, leading.start, leading.end, options)[0]
              : undefined;
            const isDerived = derived !== undefined && subqueryCtx !== undefined;
            // One JOIN: beside the table on one wrapping line. Two or more (or a
            // derived-table block): stacked beneath.
            const joinLayout = !isDerived && seg.split.joins.length === 1 ? 'inline' : 'stacked';
            return (
              <li
                key={`${seg.rawText}-${idx}`}
                className={
                  joinLayout === 'inline'
                    ? 'flex flex-wrap items-center gap-1.5'
                    : isDerived
                      ? 'flex flex-col gap-1.5'
                      : 'flex flex-col items-start gap-1.5'
                }
                data-testid={`vqe-from-item-${idx}`}
                data-derived={isDerived ? 'true' : 'false'}
                data-join-layout={joinLayout}
              >
                <FromItemRow
                  index={idx}
                  leadingText={leadingText}
                  bufferText={bufferText}
                  options={options}
                  editReady={editReady}
                  editContext={editContext}
                  derived={derived}
                  subqueryCtx={subqueryCtx}
                  cteNames={cteNames}
                />
                {seg.split.joins.length > 0 ? (
                  <ul
                    className={
                      joinLayout === 'inline'
                        ? 'm-0 flex min-w-0 max-w-full list-none flex-col p-0'
                        : 'm-0 flex max-w-full list-none flex-col gap-1.5 pl-3 p-0'
                    }
                    data-testid={`vqe-from-item-${idx}-joins`}
                  >
                    {seg.split.joins.map((join, jIdx) => (
                      <li key={`${join.start}-${jIdx}`}>
                        <JoinLine
                          bufferText={bufferText}
                          join={join}
                          options={options}
                          leadingText={leadingText}
                          testIdPrefix={`vqe-from-item-${idx}-join-${jIdx}`}
                          subqueryCtx={subqueryCtx}
                          drillLabel={`JOIN #${idx + 1}.${jIdx + 1}`}
                          editContext={editContext}
                          joinIndex={joinOffsets[idx] + jIdx}
                          compact={compact}
                          // The first join of a segment can only attach to its
                          // leading item (the row right above), so the source
                          // prefix is noise there; later joins keep it.
                          showLeading={jIdx > 0}
                        />
                      </li>
                    ))}
                  </ul>
                ) : null}
              </li>
            );
          })}
        </ul>
      )}
      </SchemaTreeDropTarget>
    </ClauseCard>
  );
}

